import React, {useEffect, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {Button, IconButton, Typography} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DevicesIcon from "@material-ui/icons/Devices";
import {ITrustedDeviceItem} from "@evennt-tech/api-v2-external";
import {useTrustedDevices} from "store/two-factor/trusted-device.store";
import {useShallow} from "zustand/react/shallow";
import {formatISODate} from "tools/formats/formatDate";
import useDateFnsLocale from "hooks/useDateFnsLocale";
import ConfirmModal from "view/components/ConfirmModal";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {useIsShowLevelOrganiser} from "hooks";
import {Roles} from "@evennt-tech/api-v2-core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import LogoutIcon from "view/images/LogoutIcon";
import FlexBox from "view/components/Layout/FlexBox";
import {formatDistanceToNow} from "date-fns";
import CancelIcon from "@material-ui/icons/Cancel";
import useStyles from "./styles";

type Props = {
  onBack: () => void;
};

const TrustedDevicesManager: React.FC<Props> = ({onBack}) => {
  const classes = useStyles();
  const {role} = useSelector(selectUser);
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const isShowLevelOrganiser = useIsShowLevelOrganiser();

  const {devices, fetchDevices, revokeDevice, revokeAll} = useTrustedDevices(
    useShallow((state) => ({
      devices: state.devices,
      fetchDevices: state.fetchDevices,
      revokeDevice: state.revokeDevice,
      revokeAll: state.revokeAll,
    }))
  );

  const [revokeTarget, setRevokeTarget] = useState<ITrustedDeviceItem | null>(
    null
  );
  const [isConfirmingRevokeAll, setIsConfirmingRevokeAll] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const isExhibitor = role === Roles.EXHIBITOR;
  const showBackButton = isExhibitor || isShowLevelOrganiser;

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;
    await revokeDevice(revokeTarget.id);
    setRevokeTarget(null);
  };

  const handleConfirmRevokeAll = async () => {
    await revokeAll();
    setIsConfirmingRevokeAll(false);
  };

  return (
    <>
      <div className={classes.backRow}>
        {showBackButton && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Typography
          style={{fontSize: showBackButton ? 18 : 24, fontWeight: 500}}
        >
          <T keyName="common.two_factor.trusted_devices.title" />
        </Typography>
      </div>

      {devices?.length ? (
        <>
          {devices.map((device) => (
            <div key={device.id} className={classes.rowCard}>
              <div className={classes.iconBox}>
                <DevicesIcon />
              </div>
              <div className={classes.rowContent}>
                <Typography className={classes.rowTitle}>
                  {device.deviceLabel || t("common.two_factor.unknown_device")}
                </Typography>
                <Typography className={classes.rowSubtitle}>
                  {device.location} ·{" "}
                  {device.browser ||
                    t("common.two_factor.trusted_devices.unknown_browser")}{" "}
                  ·{" "}
                  {device.ip ||
                    t("common.two_factor.trusted_devices.unknown_ip")}
                </Typography>
              </div>
              <FlexBox alignCenter gap={2}>
                <Typography variant="h5">
                  {t("common.two_factor.trusted_devices.last_seen", {
                    date: formatDistanceToNow(new Date(device.lastSeenAt), {
                      addSuffix: true,
                      locale,
                    }).replace(/\bminutes?\b/, (m) =>
                      m === "minute" ? "min" : "mins"
                    ),
                  })}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setRevokeTarget(device)}
                >
                  <CancelIcon color="primary" />
                </IconButton>
              </FlexBox>
            </div>
          ))}
          <Button
            variant="outlined"
            className={classes.signOutAllBtn}
            onClick={() => setIsConfirmingRevokeAll(true)}
            startIcon={<LogoutIcon className={classes.signOutAllIcon} />}
          >
            <T keyName="common.two_factor.trusted_devices.sign_out_all_button" />
          </Button>
        </>
      ) : (
        devices !== null && (
          <Typography variant="body2" className={classes.emptyText}>
            <T keyName="common.two_factor.trusted_devices.empty" />
          </Typography>
        )
      )}

      {revokeTarget && (
        <ConfirmModal
          isCalledByAnotherModal
          title={t("common.two_factor.trusted_devices.remove_title")}
          description={t("common.two_factor.trusted_devices.remove_confirm", {
            device:
              revokeTarget.deviceLabel ||
              t("common.two_factor.trusted_devices.this_device"),
          })}
          submitTitle={t("common.two_factor.trusted_devices.remove_action")}
          onClose={() => setRevokeTarget(null)}
          onSubmit={handleConfirmRevoke}
        />
      )}
      {isConfirmingRevokeAll && (
        <ConfirmModal
          isCalledByAnotherModal
          title={t("common.two_factor.trusted_devices.sign_out_all_title")}
          description={t(
            "common.two_factor.trusted_devices.sign_out_all_description"
          )}
          submitTitle={t("common.two_factor.trusted_devices.sign_out_action")}
          onClose={() => setIsConfirmingRevokeAll(false)}
          onSubmit={handleConfirmRevokeAll}
        />
      )}
    </>
  );
};

export default TrustedDevicesManager;
